import { useEffect, useState } from "react";
import { GeneralSettingsForm } from "../onboarding/components/GeneralSettings";
import Button from "../../../../components/Button";
import {
  ReviewCompetitorSet1Item,
  ReviewCompetitorSet3Item,
  ReviewCompetitorSet4Item,
  ReviewCompetitorSet5Item,
} from "../onboarding/components/Review";
import {
  CompetitorQuestion1,
  CompetitorQuestion2,
} from "./components/CompetitorQuestions";
import { AnnualBudgetForm } from "../onboarding/components/AnnualBudget";
import {
  validateAnnualBudgetForm,
  validateCompetitorSet,
  validateGeneralquestionForm,
} from "../onboarding/useValidation";
import { Page } from "../../../../components/Page";
import { api } from "../../../../helpers/api";
import { getUser } from "../../../../helpers/utils";
import { useOnboardingSave } from "../onboarding/useOnboardingSave";
import { ShowToast } from "../../../../components/toast";
import { SeasonAndEvents } from "./SeasonAndEvents/SeasonAndEvents";

const tabs = [
  { title: "General settings", name: "general" },
  { title: "Competitor Set", name: "competitor" },
  { title: "Seasons & Events", name: "seasons" },
  { title: "Budget", name: "budget" },
];

const pageTitles = {
  general: "General questions & settings",
  competitor: "Competitor Set",
  seasons: "Seasons & Events",
  budget: "Annual Budget",
};

const validate = (form: any, activeTab: string, roomTypes) => {
  switch (activeTab) {
    case "general":
      return validateGeneralquestionForm(form, roomTypes);
    case "competitor":
      return validateCompetitorSet(form);
    case "seasons":
      return { isValid: true };
    case "budget":
      return validateAnnualBudgetForm(form);
    default:
      return { isValid: true };
  }
};

function TabItem({ title, isActive, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`whitespace-nowrap w-full px-3 py-2 rounded-md hover:bg-surface-primary/50 ${
        isActive ? "!bg-surface-primary shadow-soft" : ""
      } justify-start items-center flex text-content-primary leading-[16.80px] text-sm font-medium`}
    >
      {title}
    </button>
  );
}

export function Settings({ user }) {
  const [form, setForm] = useState<any>({});
  const [roomTypes, setRoomTypes] = useState<any>([]);

  const [activeTab, setActiveTab] = useState("general");
  const validation = validate(form, activeTab, roomTypes);

  const onSaveSuccess = () => {
    ShowToast({
      type: "success",
      message: "Settings saved successfully",
    });
  };

  const { saving, onSave } = useOnboardingSave({ onNext: onSaveSuccess, user });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .getHotelData(user?.id)
      .then((res) => {
        setRoomTypes(
          (res?.roomTypes || []).map((roomType) => ({
            label: roomType.name,
            value: roomType.pmsId,
          }))
        );
      })
      .catch((error) => {
        console.log(error);
      });
  }, [user?.id]);

  const fetchOnboardingData = async (userId: string) => {
    setLoading(true);
    const onboardingResponse = await api.getOnboarding(userId);
    const { userMetadata, userPms } = onboardingResponse;
    const otas = userMetadata?.otas?.filter((ota: any) => ota !== "[]");
    setForm(
      {
        ...userMetadata,
        seasons: userMetadata?.seasons?.map((season, index) => ({
          ...season,
          id: season.id || index + 4,
        })),
        userPms,
        otas,
      } || {}
    );
    setLoading(false);
  };

  const handleCancel = () => {
    fetchOnboardingData(user.id);
  };
  const handleSave = () => {
    const { userPms, ...payload } = form;
    delete payload.userPms;
    onSave(payload);
  };

  useEffect(() => {
    if (user?.id) {
      fetchOnboardingData(user.id);
    }
  }, [user?.id]);

  return (
    <div className="flex-1 flex items-start">
      <div className="flex mx-5 my-5 flex-col p-2 overflow-auto md:w-fit bg-surface-secondary rounded-[10px] justify-start items-start gap-2">
        {tabs.map((tab) => (
          <TabItem
            key={tab.name}
            title={tab.title}
            isActive={tab.name === activeTab}
            onClick={() => setActiveTab(tab.name)}
          />
        ))}
      </div>
      <div className="w-full flex-1 tall3:h-[calc(100dvh-270px)] border-l-[0.5px] border-l-border-secondary lg:h-[calc(100dvh-160px)] flex justify-center bg-surface-primary lg:p-8">
        <div
          className={`flex-1 lg:flex-0 ${
            !["seasons"].includes(activeTab) ? "lg:max-w-[606px]" : ""
          }`}
        >
          <div className="px-4 lg:px-0 w-full lg:w-[unset] flex-col lg:flex-row justify-between items-start flex mb-5">
            <div className="flex flex-col gap-1">
              <div className="text-content-primary text-xl lg:text-2xl font-semibold font-['Inter']">
                {pageTitles[activeTab]}
              </div>
              {activeTab === "budget" && (
                <p className="hidden lg:block text-content-secondary text-sm leading-[22px]">
                  Your annual budget or estimations for the respective year
                </p>
              )}
            </div>

            {/* <div className={`flex gap-3`}>
              <Button
                disabled={saving}
                variant="outline"
                className="w-fit"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                disabled={!validation?.isValid}
                loading={saving}
                className="w-fit"
                onClick={handleSave}
              >
                Save
              </Button>
            </div> */}
          </div>
          {activeTab === "general" && (
            <div className="h-[calc(100dvh-290px)] overflow-auto px-3 flex flex-col gap-5">
              <GeneralSettingsForm
                setForm={setForm}
                form={form}
                isReview
                roomTypes={roomTypes}
                readOnly={true}
              />
            </div>
          )}
          {activeTab === "competitor" && (
            <div className="h-[calc(100dvh-275px)] tall3:h-[calc(100dvh-375px)] overflow-auto px-3">
              <ReviewCompetitorSet1Item
                showCheckmark={false}
                form={form}
                setForm={setForm}
              />
              <CompetitorQuestion1
                form={form}
                setForm={setForm}
                showCheckmark={false}
              />
              <CompetitorQuestion2
                form={form}
                setForm={setForm}
                showCheckmark={false}
              />
              <ReviewCompetitorSet3Item
                showCheckmark={false}
                form={form}
                setForm={setForm}
              />
              <ReviewCompetitorSet4Item
                showCheckmark={false}
                form={form}
                setForm={setForm}
              />
              <ReviewCompetitorSet5Item
                showCheckmark={false}
                form={form}
                setForm={setForm}
              />
            </div>
          )}
          {activeTab === "seasons" && (
            <SeasonAndEvents form={form} setForm={setForm} user={user} />
          )}
          {activeTab === "budget" && (
            <div className="">
              <AnnualBudgetForm isSettings form={form} setForm={setForm} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
